<template>
  <div class="w-100">
    <edit-license-form-card
      v-if="initialized"
      :account="account"
      :alreadyAppliedCoupon="alreadyAppliedCoupon"
      :appliedCoupon="appliedCoupon"
      :currentLicensedPlan="currentLicensedPlan"
      :licensePlanMasters="licensePlanMasters"
      :requesting="updateLicensePlanRequesting"
      :roomPlanMasters="roomPlanMasters"
      :seatPlanMasters="seatPlanMasters"
      @onClickBackButton="onClickBackButton"
      @onClickInputCouponCode="applyCouponModal = true"
      @onClickDeleteCouponCode="onClickDeleteCouponCode"
      @onClickChangeLicensePlan="onClickChangeLicensePlan"
    />
    <edit-payment-method-modal
      :modal="editPaymentMethodModalWithObject != null"
      :paymentSystem="editPaymentMethodModalWithObject"
      :paymentSystemMaster="paymentSystemMaster"
      :requesting="updatePaymentMethodRequesting"
      :title="$t('pages.indexManagementPaymentMethod.creditCardRegistration')"
      @close="editPaymentMethodModalWithObject = null"
      @createdPaymentToken="onCreatedPaymentToken"
    />
    <proa-modal
      :modal="applyCouponModal"
      title="クーポンコードの入力"
      @close="closeApplyCouponModal"
    >
      <proa-input-group label="クーポンコード">
        <template slot="input">
          <proa-input
            v-model="couponCode"
            type="text"
            dark
          />
        </template>
      </proa-input-group>
      <div v-if="invalidCouponCode" class="text-pre-wrap text-error">{{ $t('pages.editManagementLicensePlan.invalidCouponCode') }}</div>
      <template slot="footer">
        <proa-button
          :disabled="applyCouponRequesting"
          class="mr-2 default-button-width"
          @click.native="closeApplyCouponModal"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          :loading="applyCouponRequesting"
          :disabled="!couponCode"
          variant="primary"
          class="default-button-width"
          @click.native="onClickApplyCoupon"
        >
          適用
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="updateConfirmModal"
      :title="$t('common.confirm')"
      @close="updateConfirmModal = false"
    >
      <p>{{ $t('pages.editManagementLicensePlan.confirmModal.confirmMessage') }}</p>
      <div class="text-pre-wrap">{{ $t('pages.editManagementLicensePlan.precautionaryStatement') }}</div>
      <div class="text-pre-wrap">{{ $t('pages.editManagementLicensePlan.precautionaryStatementPostscript') }}</div>
      <template v-slot:footer>
        <proa-button
          @click.native="updateConfirmModal = false"
          class="mr-2"
        >
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          @click.native="onConfirmedChangeLicensePlan"
          variant="primary"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="updatedModal"
      :title="$t('pages.editManagementLicensePlan.updatedModal.title')"
      @close="$router.push({ name: 'indexManagementLicensePlan' })"
    >
      <div class="text-pre-wrap">{{ $t('pages.editManagementLicensePlan.updatedModal.body') }}</div>
      <template v-slot:footer>
        <proa-button
          @click.native="$router.push({ name: 'indexManagementLicensePlan' })"
          variant="primary"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <proa-modal
      :modal="validateChangableErrorModalWithMessage != null"
      :title="$t('common.error')"
      @close="validateChangableErrorModalWithMessage = null"
      hideCloseButton
    >
      <div class="text-pre-wrap">{{ validateChangableErrorModalWithMessage }}</div>
    </proa-modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Account from '@/store/models/admin/Account'
import LicensePlanMaster from '@/store/models/admin/LicensePlanMaster'
import PaymentSystem from '@/store/models/admin/PaymentSystem'
import PaymentSystemMaster from '@/store/models/admin/PaymentSystemMaster'
import RoomPlanMaster from '@/store/models/admin/RoomPlanMaster'
import SeatPlanMaster from '@/store/models/admin/SeatPlanMaster'

import EditLicenseFormCard from '@/components/molecules/management/licensePlan/EditFormCard'
import EditPaymentMethodModal from '@/components/molecules/management/paymentMethod/EditModal'

export default {
  name: 'EditManagementLicensePlan',
  components: {
    EditLicenseFormCard,
    EditPaymentMethodModal
  },
  computed: {
    account: function() {
      return Account.query().first()
    },
    licensePlanMasters: function() {
      return LicensePlanMaster.all()
    },
    paymentSystem: function() {
      return PaymentSystem.query().first()
    },
    paymentSystemMaster: function() {
      return PaymentSystemMaster.query().first()
    },
    roomPlanMasters: function() {
      return RoomPlanMaster.all()
    },
    seatPlanMasters: function() {
      return SeatPlanMaster.all()
    },
    returnPath: function() { return this.$route.params.returnPath },
    ...mapGetters({
      currentLicensedPlan: 'auth/currentLicensedPlan',
      httpError: 'httpError'
    })
  },
  data() {
    return {
      initialized: false,
      alreadyAppliedCoupon: false,
      appliedCoupon: null,
      couponCode: '',
      invalidCouponCode: false,
      editPaymentMethodModalWithObject: null,
      tmpNewLicensePlan: null,
      applyCouponModal: false,
      updateConfirmModal: false,
      updatedModal: false,
      applyCouponRequesting: false,
      updateLicensePlanRequesting: false,
      updatePaymentMethodRequesting: false,
      validateChangableErrorModalWithMessage: null
    }
  },
  methods: {
    closeApplyCouponModal: function() {
      if (this.applyCouponRequesting) return
      this.invalidCouponCode = false
      this.couponCode = ''
      this.applyCouponModal = false
    },
    onClickBackButton: function() {
      if (this.returnPath) {
        this.$router.push({ path: `/${this.returnPath}` })
      } else {
        this.$router.push({ name: 'management' })
      }
    },
    onClickDeleteCouponCode: function() {
      this.appliedCoupon = null
    },
    onClickChangeLicensePlan: function(newLicensePlan) {
      this.tmpNewLicensePlan = newLicensePlan
      this.updateConfirmModal = true
    },
    async onClickApplyCoupon() {
      this.invalidCouponCode = false
      this.applyCouponRequesting = true
      try {
        const response = await this.apiGetCoupon(this.couponCode)
        const { couponCode, discountRate, trialDuration } = response.data.data.attributes
        this.appliedCoupon = {
          couponCode,
          discountRate,
          trialDuration
        }
        this.applyCouponModal = false
      } catch (_) {
        this.invalidCouponCode = true
      }
      this.applyCouponRequesting = false
    },
    async onConfirmedChangeLicensePlan() {
      this.updateConfirmModal = false
      this.updateLicensePlanRequesting = true
      if (this.appliedCoupon) {
        this.tmpNewLicensePlan.couponCode = this.appliedCoupon.couponCode
      }
      await this.apiPostValidateChangableLicensePlan(this.tmpNewLicensePlan)
      this.updateLicensePlanRequesting = false

      if (this.httpError) {
        if (this.httpError?.response?.status == 422) {
          this.validateChangableErrorModalWithMessage = (this.httpError?.response?.data?.errors['license_plan'] ?? []).join('\n')
        }
      } else {
        if (!this.paymentSystem) {
          this.editPaymentMethodModalWithObject = {}
        } else if (this.paymentSystem.status != 'usePay') {
          this.editPaymentMethodModalWithObject = this.paymentSystem
        } else {
          this.execChangeLicensePlan()
        }
      }
    },
    async onCreatedPaymentToken(params) {
      this.updatePaymentMethodRequesting = true

      if (!this.paymentSystem) {
        await this.apiPostPaymentSystem(Object.assign(params, {
          paymentSystemMasterId: this.paymentSystemMaster.id
        }))
      } else {
        await this.apiPutPaymentSystem({
          id: this.paymentSystem.id,
          attributes: params
        })
      }

      if (!this.httpError && this.paymentSystem) {
        this.updatePaymentMethodRequesting = false
        this.editPaymentMethodModalWithObject = null

        this.execChangeLicensePlan()
      }
    },
    async execChangeLicensePlan() {
      this.updateLicensePlanRequesting = true
      await this.apiPutLicensePlan(this.tmpNewLicensePlan)
      this.updateLicensePlanRequesting = false

      if (!this.httpError) {
        this.updatedModal = true
      }
    },
    ...mapActions({
      apiGetAccount: 'entities/accounts/show',
      apiGetCoupon: 'coupons/show',
      apiGetLicensePlanMasters: 'entities/licensePlanMasters/index',
      apiGetPaymentSystems: 'entities/paymentSystems/index',
      apiGetPaymentSystemMasters: 'entities/paymentSystemMasters/index',
      apiGetRoomPlanMasters: 'entities/roomPlanMasters/index',
      apiGetSeatPlanMasters: 'entities/seatPlanMasters/index',
      apiGetSystemManage: 'systemManage/show',
      apiPostPaymentSystem: 'entities/paymentSystems/create',
      apiPostValidateChangableLicensePlan: 'licensePlans/validateChangable',
      apiPutLicensePlan: 'licensePlans/update',
      apiPutPaymentSystem: 'entities/paymentSystems/update'
    })
  },
  async created() {
    if (this.currentLicensedPlan.planType === 'admin') {
      this.$router.push({ name: 'management' })
      return
    } else {
      const isMaintenaceMode = await this.apiGetSystemManage()
      if (isMaintenaceMode) {
        this.$router.push({ name: 'management' })
        return
      }
    }
    await this.apiGetAccount()
    await this.apiGetPaymentSystemMasters()
    await this.apiGetLicensePlanMasters()
    await this.apiGetPaymentSystems()
    await this.apiGetRoomPlanMasters()
    await this.apiGetSeatPlanMasters()

    if (this.account.appliedCoupon) {
      this.alreadyAppliedCoupon = true
      this.appliedCoupon = this.account.appliedCoupon
    }
    this.initialized = true
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";
@import "@/styles/mediaquery";
</style>
